<template>
  <v-row>
    <v-col>
      <div class="row my-5 px-2">
        <!-- Debug info -->
        <!-- <div class="red--text">
          Debug - Number of addresses: {{ Object.keys(addresses).length }}
        </div>
        <pre class="red--text">{{ JSON.stringify(addresses, null, 2) }}</pre> -->
        <!-- End debug info -->
        
        <div class="text-h5 inline-block">
          {{ $t('Entityaddresses') }}
        </div>
        <v-spacer></v-spacer>
        <v-btn
          color="secondarygreen3"
          class="mx-1"
          depressed
          small
          :disabled="Object.keys(addresses).length >= 3"
          @click="onAddressDialog"
        >
          <v-icon
            small
            class="mr-1"
          >
            far fa-plus
          </v-icon>
          {{ $t('AddEntityAddress') }}
        </v-btn>
      </div>
      
      <!-- Address List -->
      <v-list v-if="Object.keys(addresses).length > 0">
        <v-list-item
          v-for="(address, type) in addresses"
          :key="type"
        >
          <v-list-item-content>
            <v-list-item-title>
              {{ $t(getAddressTypeLabel(type)) }}
            </v-list-item-title>
            <v-list-item-subtitle class="white-space-pre-line">
              {{ formatAddress(address) }}
            </v-list-item-subtitle>
          </v-list-item-content>
          
          <v-list-item-action>
            <v-btn
              icon
              small
              @click="onEditAddress(type, address)"
            >
              <v-icon
                small
                color="primary"
              >
                fas fa-edit
              </v-icon>
            </v-btn>
            <v-btn
              icon
              small
              @click="onDeleteAddress(type)"
            >
              <v-icon
                small
                color="red lighten-1"
              >
                fas fa-trash
              </v-icon>
            </v-btn>
          </v-list-item-action>
        </v-list-item>
      </v-list>
      <div
        v-else
        class="text-center pa-4"
      >
        No addresses found
      </div>
    </v-col>
    <SelectAddressTypeDialog
      ref="addressTypedialog"
      :existing-address-types="Object.keys(addresses)"
      @addresstypeselected="onAddressTypeSelected"
    />
  </v-row>
</template>

<script>
import SelectAddressTypeDialog from './SelectAddressTypeDialog.vue'
import internalApiClient from '../../../api/internal-client'
import adminApiClient from '../../../api/admin-api-client'
export default {
  name: 'HdEntityContactFields',
  components: {
    SelectAddressTypeDialog
  },
  props: {
    entityAddress: {
      type: Object,
      default: () => ({})
    },
    entityType: {
      type: String,
      required: true,
      validator: value => ['user', 'company', 'subdivision'].includes(value)
    },
    entityId: {
      type: [String, Number],
      required: true
    },
    companyId: {
      type: Number,
      required: false,
      default: null
    }
  },
  data() {
    return {
      addresses: {},
      resolveFnc: undefined,
      rejectFnc: undefined,
      countries: {} // Will be populated from API
    }
  },
  watch: {
    entityAddress: {
      immediate: true,
      handler(newVal) {
        // Handle null/undefined case by defaulting to empty object
        this.addresses = { ...(newVal || {}) }
      }
    }
  },
  async mounted() {
    await this.fetchCountries()
  },
  methods: {
    async fetchCountries() {
      try {
        // Replace with your actual API endpoint
        const response = await internalApiClient.getCountry()
        // console.log('response', response.data.data)
        this.countries = response.data.data.reduce((acc, country) => {
          acc[country.id] = {
            code: country.name,
            name: country.fullName
          }
          // console.log('acc', acc)
          return acc
        }, {})
      } catch (error) {
        this.$toast({ type:'error', message: this.$t('ErrorLoadingCountries') })
      }
    },

    getCountryInfo(countryCodeId) {
      return this.countries[countryCodeId] || { code: '', name: '' }
    },

    getValues() {
      return this.addresses
    },

    reset(newAddresses = {}) {
      this.addresses = { ...newAddresses }
    },

    formatAddress(address) {
      let countryText = address.countryText
      
      if (address.countryCodeId && !address.countryText) {
        const country = this.getCountryInfo(address.countryCodeId)
        countryText = country.name
      }

      const addressLines = [address.addressLine1, address.addressLine2].filter(Boolean).join(', ')
      const cityLines = [address.city, address.stateProvince, countryText].filter(Boolean).join(', ')
      const parts = [
        addressLines && `Address: ${addressLines}`,
        cityLines,
        address.postCode,
      ].filter(Boolean)
      return parts.join('\n')
    },

    getAddressTypeLabel(type) {
      // console.log('getAddressTypeLabel called with:', type)
      const labels = {
        primaryAddress: 'Primaryaddress',
        secondaryAddress: 'Secondaryaddress',
        invoicingAddress: 'Invoicingaddress'
      }
      return labels[type] || type
    },

    onAddressTypeSelected(data) {
      // console.log('onAddressTypeSelected called with:', data)
      const { type, address } = data
      this.addresses = {
        ...this.addresses,
        [type]: address
      }
      // console.log('Current addresses:', this.addresses)
      this.$emit('updateAddress', this.addresses)
      this.resolveFnc(data)
    },

    onEditAddress(type, address) {
      // If countryCodeId exists but countryCode/countryText are missing, populate them
      if (address.countryCodeId && this.countries[address.countryCodeId]) {
        address = {
          ...address,
          countryCode: this.countries[address.countryCodeId].code,
          countryText: this.countries[address.countryCodeId].name
        }
      }
      
      this.$refs.addressTypedialog.editAddress(type, address)
      return new Promise((resolve, reject) => {
        this.resolveFnc = resolve
        this.rejectFnc = reject
      }).then((data) => {
        this.addresses = {
          ...this.addresses,
          [data.type]: data.address
        }
        this.$emit('updateAddress', this.addresses)
      }).catch(() => {
        // Handle cancel
      })
    },

    async onDeleteAddress(type) {
      this.$confirm({
        color: 'warning',
        title: this.$t('Delete'),
        text: this.$t('ConfirmDeleteAddress'),
        confirmButtonText: this.$t('Delete')
      })
      .then(async () => {
        try {
          // Get the address type ID mapping
          const addressTypeIdMap = {
            primaryAddress: 1,
            secondaryAddress: 2,
            invoicingAddress: 3
          }

          // Get entity addresses based on entity type
          let response
          switch (this.entityType) {
            case 'user':
              response = await adminApiClient.getUsersEntityAddresses(this.entityId)
              break
            case 'company':
              response = await adminApiClient.getCompanyEntityAddresses(this.entityId)
              break
            case 'subdivision':
              response = await adminApiClient.getSubdivisionEntityAddresses(this.companyId, this.entityId)
              break
          }

          const addressToDelete = response.data.find(
            address => address.addressTypeId === addressTypeIdMap[type]
          )
          if (addressToDelete) {
            await adminApiClient.deleteEntityAddress(addressToDelete.addressId)
          }

          // Update local state
          const updatedAddresses = { ...this.addresses }
          delete updatedAddresses[type]
          this.addresses = updatedAddresses
          this.$emit('updateAddress', this.addresses)
          this.$toast({ type: 'success', message: this.$t('AddressDeleted') })
        } catch (error) {
          this.$toast({ type:'error', message: this.$t('ErrorDeletingAddress') })
        }
      })
      .catch(() => {
        // User cancelled the deletion
      })
    },

    onAddressDialog() {
      this.$refs.addressTypedialog.open = true
      return new Promise((resolve, reject) => {
        this.resolveFnc = resolve
        this.rejectFnc = reject
      })
    },
  }
}
</script>

<style>
.white-space-pre-line {
  white-space: pre-line;
}
</style>